import React, {forwardRef, useImperativeHandle, useState} from 'react';
import cn from 'classnames';
import produce from 'immer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {Button} from 'components';
import './UserDefined.scss';

interface IUserDefined {
    [key: string]: string
}

interface IField {
    key: string,
    value: string,
}

interface IUserDefinedProps {
    defaultValue?: IUserDefined,
}

export interface IUserDefinedAPI {
    validateAndGetValue: () => {
        isValid: boolean,
        userDefined: IUserDefined,
    },
    setUserDefined(userDef),
}

interface IUserDefinedState {
    fields: IField[],
    isShowingErrors?: boolean,
}

export const UserDefined = forwardRef<IUserDefinedAPI, IUserDefinedProps>(function UserDefined(props, ref) {
    const { defaultValue } = props;

    const [state, setState] = useState<IUserDefinedState>({
        fields: convertObjToFields(defaultValue),
    })
    const { fields, isShowingErrors } = state;

    function convertObjToFields(userDefined: IUserDefined): IField[] {
        let fields: IField[] = [];
        if (userDefined) {
            fields = Object.entries(userDefined).map((entry) => {
                return {
                    key: entry[0],
                    value: entry[1],
                }
            })
        }
        return fields;
    } 

    function convertFieldsToObj(fieldsToConvert: IField[]): IUserDefined {
        let out: IUserDefined = {};
        for (const f of fieldsToConvert) {
            if (f.key && f.value) {
                out[f.key] = f.value;
            }
        }
        return out;
    }

    function onAddRow() {
        setState(produce(draft => {
            draft.fields.push({key: '', value: ''});
        }));
    }

    function onRemoveRow(index: number) {
        setState(produce(draft => {
            draft.fields.splice(index, 1);
        }))   
    }

    function setUserDefinedKey(key: string, i: number) {
        setState(produce(draft => {
            draft.fields[i].key = key;
        }));
    }

    function setUserDefinedValue(value: string, i: number) {
        setState(produce(draft => {
            draft.fields[i].value = value;
        }));
    }

    function isFieldValid(f: IField): boolean {
        return !!(f.key && f.value);
    }

    function isValid(): boolean {
        for (const f of fields) {
            if (!isFieldValid(f)) {
                return false;
            }
        }
        return true;
    }

    // -------------------- Public API  ---------------------
    // A parent component can call these methods by using a ref like:
    //     <UserDefined ref={userDefined.Ref} />
    //     ...
    //     const result = userDefined.current.validateAndGetValue();
    useImperativeHandle(ref, () => ({
        validateAndGetValue: () => {
            // Update our state so that we now show any errors
            setState(produce(draft => {
                draft.isShowingErrors = true;
            }));

            return {
                userDefined: convertFieldsToObj(fields),
                isValid: isValid(),
            }
        },
        setUserDefined: (userDef) => {
            // Update state with user defined from update file
            setState(produce(draft => {
                draft.fields = convertObjToFields(userDef);
            }));
        },
    }));

    return (
        <div className="userDefined">
            {fields.length > 0 && (
                <div className="userDefined_fields">
                    {fields.map((field, i) => (
                        <div className="userDefined_row" key={i}>
                            <div className="userDefined_col">
                                <div className="formItem_label">
                                    Key
                                </div>
                                <input
                                    className={cn("userDefined_input formItem_input", {
                                        error: isShowingErrors && !field.key,
                                    })}
                                    type="text"
                                    value={field.key}
                                    onChange={event => setUserDefinedKey(event.target.value, i)}
                                />
                            </div>
                            <div className="userDefined_col userDefined_colValue">
                                <div className="formItem_label">
                                    Value
                                </div>
                                <input
                                    className={cn("userDefined_input formItem_input", {
                                        error: isShowingErrors && !field.value,
                                    })}
                                    type="text"
                                    value={field.value}
                                    onChange={event => setUserDefinedValue(event.target.value, i)}
                                />
                            </div>
                            <Button
                                className="userDefined_delete"
                                theme="icon"
                                size="small"
                                onClick={() => onRemoveRow(i)}
                            >
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
            {isShowingErrors && !isValid() && (
                <div className="userDefined_error">
                    Please enter a key value pair for each User Defined field.
                </div>
            )}
            <Button
                onClick={onAddRow}
                theme="blueOutline"
                size="small"
                className="userDefined_addRowButton"
            >
                Add Field
            </Button>
        </div>
    );
});